import { ProxyGraphClients, abgGraphqlClient as graphqlClient } from '@atlassian/temp-client-utils';
import type {
  FetchNativeProductCatalogueWithOfferingAndPricingPlansQuery,
  FetchNativeProductCatalogueWithOfferingAndPricingPlansQueryVariables,
} from '@css-xp/graphtypes/src/generated-types-sxp';
import { FilterOption } from '@css-xp/graphtypes/src/generated-types-new';
import { isStatuspage } from '@atlassian/cssxp-utils-product';
import { fetchNativeProductCatalogueWithOfferingAndPricingPlans } from '@css-xp/ppc-api';
import { CATALOGUE_CONSTANTS, CATALOG_EXCLUDE_PRODUCTS } from '@css-xp/ppc-constants';
import type { State } from '../types';
import { truthy } from '../../utils';
import { getPricingPlanFilterOptionsForNativeCatalog } from './get-catalog-pricing-plan-filters';
import { getOfferingAndPricingPlanMapForStore } from './get-offerings-and-pricing-plan-map-for-store';
import { getStatuspageAddonsOfferingRelationshipMap } from './get-statuspage-addons-offering-relationship-map';

export async function getNativeProductsWithOfferingsAndPricingPlans({
  filterOptions,
  includeExperimentalProducts = false,
  isMultiCurrencyEnabled = false,
  isAddOnsSearchEnabled = false,
  isStatuspageEnabled = false,
  isTeamWorkCollectionEnabled = false,
  isStrategyCollectionEnabled = false,
}: {
  filterOptions?: [FilterOption];
  includeExperimentalProducts?: boolean;
  isMultiCurrencyEnabled?: boolean;
  isAddOnsSearchEnabled?: boolean;
  isStatuspageEnabled?: boolean;
  isTeamWorkCollectionEnabled?: boolean;
  isStrategyCollectionEnabled?: boolean;
}) {
  const productIdPricingPlanMap: State['offeringsAndpricingPlans'] = {};
  const response = await graphqlClient(ProxyGraphClients.SXP).query<
    FetchNativeProductCatalogueWithOfferingAndPricingPlansQuery,
    FetchNativeProductCatalogueWithOfferingAndPricingPlansQueryVariables
  >({
    query: fetchNativeProductCatalogueWithOfferingAndPricingPlans,
    variables: {
      pageParams: {
        size: CATALOGUE_CONSTANTS.DEFAULT_ROWS_PER_PAGE_FOR_CATALOGUE,
      },
      pricingPlanFilterOptions: getPricingPlanFilterOptionsForNativeCatalog(isMultiCurrencyEnabled),
      filterOptions,
      includeExperimentalProducts,
      isAddOnsSearchEnabled,
      isStatuspageEnabled,
      isTeamWorkCollectionEnabled,
      isStrategyCollectionEnabled,
    },
    fetchPolicy: 'network-only',
  });

  const catalogProducts = response?.data?.nativeCatalog?.nodes ?? [];

  if (response?.data?.nativeCatalog) {
    response.data.nativeCatalog.nodes = catalogProducts?.filter(
      (product) => product && !CATALOG_EXCLUDE_PRODUCTS.includes(product?.id)
    );
  }

  const notEmptyCatalogProducts = catalogProducts.filter(truthy);

  const statuspageNativeCatalogProduct = notEmptyCatalogProducts?.find((product) => isStatuspage(product?.id));
  const statuspageAddonsOfferingsMap = getStatuspageAddonsOfferingRelationshipMap(statuspageNativeCatalogProduct);
  const offeringAndPricingPlanIdMap = getOfferingAndPricingPlanMapForStore(notEmptyCatalogProducts);

  return {
    nativeProduct: response,
    offeringsAndpricingPlans: offeringAndPricingPlanIdMap,
    statuspageAddonsOfferingsMap: statuspageAddonsOfferingsMap,
  };
}
